// Comissões e Recebimentos — contas do módulo novo, ligadas aos campos que já existem:
// - comissoes: comissao_total, reporting_ang/venda, val_consultor_ang/venda (via src/Contas.js)
// - recebimentos_comissao, uma linha por consultor:
//     valor_cliente + data_pago_cliente  → Comissão Imobiliária recebida (parte desse consultor)
//     reportado + data_reportado         → Reportado à RE/MAX (mês reportado)
//     valor_consultor + data_pago_consultor → Recebimento de agente
// Regras da Sónia (out/2026): conta só a parte da equipa; líder vê todos, consultor só a sua linha;
// Reporting = Reportada + Por Reportar e Comissão = Recebida + A Receber, sempre sobre os mesmos negócios.
import { contasNegocio, FASE_PREVISAO, FASE_CONCLUIDO, dataGarantida, dataPrevisao } from "./Contas";

export const FASES = [["proposta", "Proposta aceite"], ["cpcv", "CPCV assinado"], ["agendada", "Escritura agendada"], ["realizada", "Escritura realizada"], ["recebido", "Recebido"]];
export const nomeFase = (f) => (FASES.find(x => x[0] === f) || [null, "—"])[1];
const num = (v) => Number(v || 0);
export const r2 = (v) => Math.round(num(v) * 100) / 100;
export const iniciais = (nome) => { const p = String(nome || "").trim().split(/\s+/).filter(Boolean); return p.length ? (p[0][0] + (p.length > 1 ? p[p.length - 1][0] : "")).toUpperCase() : "?"; };
export const dataRecebimento = (r) => r.data_pago_cliente || r.data_evento || String(r.created_at || "").slice(0, 10);
export const dataAgente = (r) => r.data_pago_consultor || r.data_pago_cliente || r.data_evento || String(r.created_at || "").slice(0, 10);

function faseBase(n) {
  if (FASE_PREVISAO.includes(n.estado)) return "proposta";
  if (n.estado === "escritura_agendada") return "agendada";
  if (FASE_CONCLUIDO.includes(n.estado)) return "realizada";
  return "cpcv";
}

// Um negócio visto pelo módulo: uma linha por consultor da equipa (o pleno junta os dois lados) e uma por agência de fora.
// Devolve null se o negócio não conta (apagado, ou cancelado sem valor cobrado).
export function negocioCR(n, ctx) {
  if (!n || n.deleted_at) return null;
  const cn = contasNegocio(n, ctx);
  if (!cn.fase) return null;
  const recs = (ctx.recebimentos || []).filter(r => !r.deleted_at && r.nota_credito_estado !== "emitida" && (r.negocio_id === n.id || (cn.comissao && r.comissao_id === cn.comissao.id)));
  const linhas = [];
  cn.linhas.forEach(l => {
    const f = l.pctLado === "ang" ? "A" : "C";
    if (l.externo) { linhas.push({ k: "x:" + f, externo: true, nome: l.imobiliaria || l.nome || "Outra agência", fs: [f], r: l.reporting, c: 0 }); return; }
    const k = l.consultorId || l.nome, ja = linhas.find(o => o.k === k);
    if (ja) { ja.fs.push(f); ja.r = r2(num(ja.r) + num(l.reporting)); ja.c = r2(num(ja.c) + num(l.valor)); ja.semDados = ja.semDados || l.reporting === null; }
    else linhas.push({ k, externo: false, id: l.consultorId || null, nome: l.nome || "", fs: [f], r: l.reporting, c: num(l.valor), semDados: l.reporting === null });
  });
  const internas = linhas.filter(o => !o.externo);
  const deQuem = (r) => internas.find(o => (o.id && r.consultor_id === o.id) || (!r.consultor_id && r.consultor_nome && r.consultor_nome === o.nome));
  internas.forEach(o => { o.recs = []; });
  const semConsultor = [];
  recs.forEach(r => { const o = deQuem(r); if (o) o.recs.push(r); else semConsultor.push(r); });
  internas.forEach(o => {
    o.r = num(o.r);
    o.rec = r2(o.recs.reduce((s, r) => s + num(r.valor_cliente), 0));
    o.reportada = r2(o.recs.filter(r => r.reportado).reduce((s, r) => s + num(r.valor_cliente), 0));
    o.cRec = r2(o.recs.reduce((s, r) => s + num(r.valor_consultor), 0));
    o.cMax = o.r > 0 ? r2(o.rec * o.c / o.r) : 0; // o agente só recebe sobre o que a agência já recebeu da parte dele
    o.pct = o.r > 0 ? Math.round(o.c / o.r * 1000) / 10 : null;
  });
  const fase = cn.cancelado ? "realizada" : faseBase(n);
  return { n, comissao: cn.comissao, total: num(cn.total), linhas, internas, semConsultor, fase, cancelado: !!cn.cancelado,
    arr: n.tipo === "arrendamento", valor: num(n.valor_venda), data: String((fase === "proposta" ? dataPrevisao(n) : dataGarantida(n)) || "").slice(0, 10) };
}

// Linhas que contam para quem está a ver: ids vazio = todos os consultores da equipa (líder)
export const minhas = (nc, ids) => nc.internas.filter(o => !ids || !ids.length || ids.includes(o.id));

export function contasCR(nc, ids) {
  const ls = minhas(nc, ids), S = (k) => r2(ls.reduce((s, o) => s + num(o[k]), 0));
  const rep = S("r"), cons = S("c"), rec = S("rec"), reportada = S("reportada"), cRec = S("cRec");
  const cob = nc.fase === "proposta" ? "Previsão" : rep > 0 && rec >= rep - 0.01 ? "Recebido" : rec > 0.005 ? "Parcial" : "Por receber";
  return { linhas: ls, rep, cons, rec, reportada, cRec, cob, fase: cob === "Recebido" && nc.fase === "realizada" ? "recebido" : nc.fase, semDados: ls.some(o => o.semDados) };
}

// Somas que batem sempre: reporting = reportada + recebida por reportar + por receber; comissão = recebida + a receber.
// A previsão (proposta aceite) fica à parte e nunca entra nestas contas.
export function somarCR(lista, ids) {
  const s = { n: 0, rep: 0, reportada: 0, recNaoRep: 0, porReceber: 0, cons: 0, consRec: 0, previsao: 0, nPrev: 0 };
  lista.forEach(nc => {
    const c = contasCR(nc, ids);
    if (!c.linhas.length) return;
    if (c.fase === "proposta") { s.previsao += c.rep; s.nPrev++; return; }
    s.n++; s.rep += c.rep; s.reportada += c.reportada; s.recNaoRep += c.rec - c.reportada; s.porReceber += c.rep - c.rec; s.cons += c.cons; s.consRec += c.cRec;
  });
  Object.keys(s).forEach(k => { s[k] = r2(s[k]); });
  return s;
}

// Movimentos, uma linha por registo e por consultor:
// agência = comissão imobiliária recebida (valor_cliente); agente = recebimento de agente (valor_consultor)
export function movimentosCR(lista, ids) {
  const agencia = [], agente = [];
  lista.forEach(nc => minhas(nc, ids).forEach(o => o.recs.forEach(r => {
    if (num(r.valor_cliente) > 0) agencia.push({ nc, o, r, v: num(r.valor_cliente), data: dataRecebimento(r), mes: r.reportado && r.data_reportado ? String(r.data_reportado).slice(0, 7) : null });
    if (num(r.valor_consultor) > 0) agente.push({ nc, o, r, v: num(r.valor_consultor), data: dataAgente(r), semData: !r.data_pago_consultor });
  })));
  return { agencia, agente };
}

// Quanto é que este agente pode receber agora neste negócio (0 enquanto a agência não receber a parte dele)
export const podeReceberAgente = (o) => Math.max(0, r2(o.cMax - o.cRec));
// Quanto falta a agência receber da parte deste consultor
export const faltaAgencia = (o) => Math.max(0, r2(o.r - o.rec));
